/**
 * HistoryPanel - Analysis history with report viewing
 *
 * Connects to real API to fetch report history and supports:
 * - Viewing reports from the API
 * - Deleting reports
 * - Downloading reports
 * - Re-analyzing companies
 * - Local caching for faster loading
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import type { Report } from '../../types'
import { listReports, downloadReport, deleteReport, type ReportListItem } from '../../services/api'

const CACHE_KEY = 'pe-diligence-reports-cache'
const CACHE_MAX_AGE = 5 * 60 * 1000 // 5 minutes

interface CachedData {
  reports: ReportListItem[]
  timestamp: number
}

interface HistoryPanelProps {
  onSelectCompany?: (ticker: string, companyName: string) => void
  onReanalyze?: (ticker: string) => void
}

// Extended report type to include real content
interface ExtendedReport extends Report {
  content?: string
  report_id?: string
  download_url?: string
}

// Load cached reports from localStorage
function loadCachedReports(): CachedData | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY)
    if (cached) {
      return JSON.parse(cached)
    }
  } catch (e) {
    console.warn('Failed to load cached reports:', e)
  }
  return null
}

// Save reports to localStorage cache
function saveCachedReports(reports: ReportListItem[]) {
  try {
    const data: CachedData = {
      reports,
      timestamp: Date.now(),
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(data))
  } catch (e) {
    console.warn('Failed to cache reports:', e)
  }
}

// Format cache age
function formatCacheAge(timestamp: number): string {
  const diffMs = Date.now() - timestamp
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffMs / 60000)

  if (diffSecs < 10) return 'just now'
  if (diffSecs < 60) return `${diffSecs}s ago`
  if (diffMins < 60) return `${diffMins}m ago`
  return 'over 1h ago'
}

export function HistoryPanel({ onSelectCompany, onReanalyze }: HistoryPanelProps) {
  const [viewingReport, setViewingReport] = useState<ExtendedReport | null>(null)
  const [reports, setReports] = useState<ReportListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const mountedRef = useRef(true)

  // Load reports from API (with optional background refresh)
  const loadReports = useCallback(async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }
    setError(null)

    try {
      const result = await listReports()
      if (!mountedRef.current) return

      if (result.data?.reports) {
        setReports(result.data.reports)
        setLastUpdated(Date.now())
        saveCachedReports(result.data.reports)
      } else if (result.error) {
        // Only show error if not background refresh and no cached data
        if (!isBackgroundRefresh) {
          setError(result.error)
        }
      }
    } catch (err) {
      if (!mountedRef.current) return
      if (!isBackgroundRefresh) {
        setError(err instanceof Error ? err.message : 'Failed to load reports')
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false)
        setRefreshing(false)
      }
    }
  }, [])

  // Load cached data immediately, then refresh from API
  useEffect(() => {
    mountedRef.current = true

    // Load cached data immediately
    const cached = loadCachedReports()
    if (cached && cached.reports.length > 0) {
      setReports(cached.reports)
      setLastUpdated(cached.timestamp)
      setLoading(false)

      // If cache is fresh enough, don't refresh immediately
      const cacheAge = Date.now() - cached.timestamp
      if (cacheAge < CACHE_MAX_AGE) {
        // Still refresh in background after a short delay
        setTimeout(() => {
          if (mountedRef.current) {
            loadReports(true)
          }
        }, 1000)
      } else {
        // Cache is stale, refresh immediately
        loadReports(true)
      }
    } else {
      // No cache, load fresh
      loadReports(false)
    }

    return () => {
      mountedRef.current = false
    }
  }, [loadReports])

  // Handle view report
  const handleViewReport = useCallback(async (report: ReportListItem) => {
    try {
      // Use content from report if available, otherwise fetch it
      let content = report.content
      if (!content) {
        content = await downloadReport(report.report_id) || 'Failed to load report content'
      }

      const extendedReport: ExtendedReport = {
        id: report.report_id,
        ticker: report.ticker,
        companyName: report.title || report.ticker,
        reportType: report.type || 'full',
        createdAt: report.created_at || new Date().toISOString(),
        status: report.status || 'completed',
        filePath: `/api/reports/${report.report_id}/download`,
        content: content,
        report_id: report.report_id,
        download_url: `/reports/${report.report_id}/download`,
      }

      setViewingReport(extendedReport)
    } catch (err) {
      console.error('Error viewing report:', err)
    }
  }, [])

  // Handle download report
  const handleDownload = useCallback((report: ExtendedReport) => {
    const content = report.content || 'Report content not available'
    const blob = new Blob([content], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${report.ticker}_DD_Report_${new Date(report.createdAt).toISOString().split('T')[0]}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [])

  // Handle delete report
  const handleDelete = useCallback(async (reportId: string) => {
    if (!confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
      return
    }

    setDeletingId(reportId)
    try {
      const result = await deleteReport(reportId)
      if (result.data?.success) {
        // Remove from local state and update cache
        setReports(prev => {
          const updated = prev.filter(r => r.report_id !== reportId)
          saveCachedReports(updated)
          return updated
        })
        setLastUpdated(Date.now())
      } else {
        alert(result.error || 'Failed to delete report')
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete report')
    } finally {
      setDeletingId(null)
    }
  }, [])

  // Handle clear all history
  const handleClearHistory = useCallback(async () => {
    if (!confirm('Are you sure you want to delete ALL reports? This action cannot be undone.')) {
      return
    }

    // Delete all reports one by one
    for (const report of reports) {
      await deleteReport(report.report_id)
    }

    // Clear cache and reload the list
    localStorage.removeItem(CACHE_KEY)
    loadReports(false)
  }, [reports, loadReports])

  // Close report viewer
  const handleCloseViewer = useCallback(() => {
    setViewingReport(null)
  }, [])

  // Format date from ISO string or Unix timestamp - always show actual date/time
  const formatDate = (dateValue: string | number | undefined) => {
    if (!dateValue) return 'Unknown'

    let date: Date
    if (typeof dateValue === 'string') {
      date = new Date(dateValue)
    } else {
      date = new Date(dateValue * 1000)
    }

    if (isNaN(date.getTime())) return 'Unknown'

    // Always show actual date and time in 24-hour format (e.g., "Jan 19, 2026, 13:12")
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  // Format size - estimate from content length if no size provided
  const formatSize = (report: ReportListItem) => {
    const bytes = report.size || (report.content ? new Blob([report.content]).size : 0)
    if (bytes === 0) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // If viewing a report, show the report viewer
  if (viewingReport) {
    return (
      <ReportViewer
        report={viewingReport}
        onClose={handleCloseViewer}
        onDownload={handleDownload}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-term-text">
            Analysis History
          </h2>
          {lastUpdated && (
            <span className="text-xs text-term-textDim flex items-center gap-1">
              {refreshing && (
                <svg className="animate-spin w-3 h-3 text-term-info" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {refreshing ? 'Refreshing...' : `Updated ${formatCacheAge(lastUpdated)}`}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => loadReports(false)}
            className="term-button text-sm"
            disabled={loading || refreshing}
          >
            {loading ? 'Loading...' : refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={handleClearHistory}
            className="term-button text-sm text-term-error"
            disabled={reports.length === 0}
          >
            Clear History
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="term-box p-4 border-term-error bg-term-error/10">
          <div className="flex items-center gap-2 text-term-error">
            <span>Error:</span>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="term-box p-4">
          <div className="text-term-textDim text-xs mb-1">Total Reports</div>
          <div className="text-2xl font-bold text-term-text">{reports.length}</div>
        </div>
        <div className="term-box p-4">
          <div className="text-term-textDim text-xs mb-1">Total Size</div>
          <div className="text-2xl font-bold text-term-success">
            {(() => {
              const totalBytes = reports.reduce((sum, r) =>
                sum + (r.size || (r.content ? new Blob([r.content]).size : 0)), 0)
              if (totalBytes === 0) return '-'
              if (totalBytes < 1024) return `${totalBytes} B`
              if (totalBytes < 1024 * 1024) return `${(totalBytes / 1024).toFixed(1)} KB`
              return `${(totalBytes / (1024 * 1024)).toFixed(1)} MB`
            })()}
          </div>
        </div>
        <div className="term-box p-4">
          <div className="text-term-textDim text-xs mb-1">Unique Tickers</div>
          <div className="text-2xl font-bold text-term-info">
            {new Set(reports.map(r => r.ticker)).size}
          </div>
        </div>
        <div className="term-box p-4">
          <div className="text-term-textDim text-xs mb-1">This Week</div>
          <div className="text-2xl font-bold text-term-warning">
            {reports.filter(r => {
              const dateStr = r.created_at || r.modified
              if (!dateStr) return false
              const date = typeof dateStr === 'string' ? new Date(dateStr) : new Date(dateStr * 1000)
              const diff = Date.now() - date.getTime()
              return diff < 7 * 24 * 60 * 60 * 1000
            }).length}
          </div>
        </div>
      </div>

      {/* History Table */}
      <div className="term-box">
        {loading ? (
          <div className="p-8 text-center text-term-textDim">
            Loading reports...
          </div>
        ) : reports.length === 0 ? (
          <div className="p-8 text-center text-term-textDim">
            No reports found. Generate a DD report to see it here.
          </div>
        ) : (
          <table className="term-table">
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Report Title</th>
                <th>Generated</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.report_id} className="cursor-pointer hover:bg-term-bgLight/50">
                  <td
                    className="font-medium text-term-primary hover:underline cursor-pointer"
                    onClick={() => onSelectCompany?.(report.ticker, report.ticker)}
                  >
                    {report.ticker}
                  </td>
                  <td className="text-term-textDim text-sm">
                    {report.title || `${report.ticker} DD Report`}
                  </td>
                  <td className="text-term-textDim">{formatDate(report.created_at || report.modified)}</td>
                  <td className="text-term-textDim capitalize">{report.type || 'comprehensive'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewReport(report)}
                        className="text-term-primary hover:underline text-sm"
                      >
                        View
                      </button>
                      <a
                        href={`/api/reports/${report.report_id}/download`}
                        download={`${report.ticker}_DD_Report.md`}
                        className="text-term-success hover:underline text-sm"
                      >
                        Download
                      </a>
                      <button
                        onClick={() => onReanalyze?.(report.ticker)}
                        className="text-term-info hover:underline text-sm"
                      >
                        Re-analyze
                      </button>
                      <button
                        onClick={() => handleDelete(report.report_id)}
                        disabled={deletingId === report.report_id}
                        className="text-term-error hover:underline text-sm disabled:opacity-50"
                      >
                        {deletingId === report.report_id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Quick Actions */}
      <div className="term-box p-4">
        <h3 className="text-sm font-medium text-term-text mb-3">Quick Actions</h3>
        <div className="flex gap-3">
          <button
            className="term-button flex-1"
            onClick={() => loadReports(false)}
            disabled={loading || refreshing}
          >
            {loading || refreshing ? 'Refreshing...' : 'Refresh List'}
          </button>
          <button className="term-button flex-1" disabled>
            Compare Selected
          </button>
          <button className="term-button flex-1" disabled>
            Batch Export
          </button>
        </div>
      </div>
    </div>
  )
}

// Report Viewer Component
interface ReportViewerProps {
  report: ExtendedReport
  onClose: () => void
  onDownload: (report: ExtendedReport) => void
}

function ReportViewer({ report, onClose, onDownload }: ReportViewerProps) {
  const content = report.content || 'Report content not available'

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-term-text">
          {report.ticker} - DD Report
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => onDownload(report)}
            className="term-button term-button-success"
          >
            Download
          </button>
          <button
            onClick={onClose}
            className="term-button"
          >
            Close
          </button>
        </div>
      </div>

      {/* Report Meta */}
      <div className="flex gap-4 text-sm text-term-textDim">
        <span>Company: {report.companyName}</span>
        <span>-</span>
        <span>Generated: {new Date(report.createdAt).toLocaleString()}</span>
        <span>-</span>
        <span className="capitalize">Type: {report.reportType}</span>
      </div>

      {/* Report Content */}
      <div className="term-box p-6 bg-term-bgDark max-h-[60vh] overflow-auto">
        <pre className="whitespace-pre-wrap font-mono text-sm text-term-text leading-relaxed">
          {content}
        </pre>
      </div>
    </div>
  )
}
